import i18n, { TFunction } from 'i18next';
import moment, { Moment } from 'moment';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { descriptorDimensions as descriptorDimensionsUtil } from '@r6digital/rapidstor-types/react/util/UnitTypeDisplay';
import InsuranceCoverage from '@r6digital/rapidstor-types/types/InsuranceCoverage';
import { BaseReservation } from '@r6digital/rapidstor-types/types/Reservation';

import { CorporationCode, CorpTranslation, getCorpTranslations } from '@/core/translations/helpers';
import { SETTINGS_TEXT_KEYS } from '@/core/translations/settingsTextKeys';

import { formatCurrency, formatPriceLabel } from './currency';

export type SettingsTextHelper = keyof typeof SETTINGS_TEXT_KEYS;

/**
 * An operator's Automate override for a settings-backed phrase, from any layer of
 * the chain (language < corporation < location < snippet). `undefined` falls the
 * helper through to the bundled corporation copy and then to the location's own
 * RapidStor setting, exactly as before.
 *
 * The catalog stores the whole path dotted; i18next addresses a namespace as
 * `ns:key`, so only the first separator differs. See `settingsTextKeys` for why
 * every key must be in the `rapidstor` namespace.
 */
const overrideFor = (helper: SettingsTextHelper): string | undefined => {
    const value = i18n.t(SETTINGS_TEXT_KEYS[helper].replace('.', ':'), {
        defaultValue: '',
    });
    return typeof value === 'string' && value.trim() ? value : undefined;
};

export const payNowButtonText = (location: Location): string => {
    const override = overrideFor('payNowButtonText');
    if (override) return override;

    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode);

    if (!corpTranslations) {
        return location.rapidstor.payNowButtonText;
    }

    if (!('rapidstor' in corpTranslations)) {
        return location.rapidstor.payNowButtonText;
    }

    if ('payNowButtonText' in corpTranslations.rapidstor) {
        return corpTranslations.rapidstor.payNowButtonText;
    }

    return location.rapidstor.payNowButtonText;
};

export const payLaterButtonText = (location: Location): string => {
    const override = overrideFor('payLaterButtonText');
    if (override) return override;

    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode);

    if (!corpTranslations) {
        return location.rapidstor.payLaterButtonText;
    }

    if (!('rapidstor' in corpTranslations)) {
        return location.rapidstor.payLaterButtonText;
    }

    if ('payLaterButtonText' in corpTranslations.rapidstor) {
        return corpTranslations.rapidstor.payLaterButtonText;
    }

    return location.rapidstor.payLaterButtonText;
};

export const saveQuoteButtonText = (location: Location): string => {
    const override = overrideFor('saveQuoteButtonText');
    if (override) return override;

    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode);

    if (!corpTranslations) {
        return location.rapidstor.saveQuoteButtonText;
    }

    if (!('rapidstor' in corpTranslations)) {
        return location.rapidstor.saveQuoteButtonText;
    }

    if ('saveQuoteButtonText' in corpTranslations.rapidstor) {
        return corpTranslations.rapidstor.saveQuoteButtonText;
    }

    return location.rapidstor.saveQuoteButtonText;
};

export const showPricesButtonText = (location: Location, preferRapidStorText = false): string => {
    // preferRapidStorText asks for the operator's own setting text in place of any
    // translation, so it bypasses the Automate override as well as the corp bundle.
    const override = preferRapidStorText
        ? undefined
        : overrideFor('showPricesButtonText');
    if (override) return override;

    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode) as CorpTranslation;

    if (!corpTranslations || preferRapidStorText) {
        return location.rapidstor.showPricesButtonText;
    }

    if (!corpTranslations.rapidstor?.showPricesButtonText) {
        return location.rapidstor.showPricesButtonText;
    } if (corpTranslations.rapidstor?.showPricesButtonText) {
        return corpTranslations.rapidstor.showPricesButtonText as string;
    }

    if (corpTranslations.contact_card?.showPrices?.title) {
        return corpTranslations.contact_card.showPrices.title as string;
    }

    return location.rapidstor.showPricesButtonText;
};

export const showPricesTitleText = (location: Location): string => {
    const override = overrideFor('showPricesTitleText');
    if (override) return override;

    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode) as CorpTranslation;

    if (!corpTranslations) {
        return location.rapidstor.showPricesButtonText;
    }

    if (corpTranslations.contact_card?.showPrices?.title) {
        return corpTranslations.contact_card.showPrices.title as string;
    }

    return location.rapidstor.showPricesButtonText;
};

export const noAvailableUnitText = (location: Location): string => {
    const override = overrideFor('noAvailableUnitText');
    if (override) return override;

    const corpTranslations = getCorpTranslations(location.corpCode as CorporationCode);

    if (!corpTranslations) {
        return location.rapidstor.noAvailableUnitsText;
    }

    if (!('rapidstor' in corpTranslations)) {
        return location.rapidstor.noAvailableUnitsText;
    }

    if ('noAvailableUnitsText' in corpTranslations.rapidstor) {
        return corpTranslations.rapidstor.noAvailableUnitsText;
    }

    return location.rapidstor.noAvailableUnitsText ?? i18n.t('cta:call_us_now');
};

export type RapidstorTextSource = 'setting' | 'translation';

/**
 * Whether a settings-text helper above resolves to the location's RapidStor
 * setting or to a translation override — an Automate one from any layer of the
 * chain, or the bundled corporation copy. The build step tags elements rendering
 * these helpers with `data-rs-setting-dynamic`; `installSettingsTextMarker` calls
 * this at runtime so Automate's translate inspector only flags text that is
 * *actually* coming from a setting (translations take precedence and are not
 * flagged).
 *
 * Mirrors the precedence in each helper above — keep in sync.
 */
export const rapidstorTextSource = (
    helper: string,
    location: Location,
): RapidstorTextSource => {
    // An Automate override outranks every branch below, so it settles the answer
    // for any helper that accepts one.
    if (helper in SETTINGS_TEXT_KEYS
        && overrideFor(helper as SettingsTextHelper) !== undefined) {
        return 'translation';
    }

    const corpTranslations = getCorpTranslations(
        location.corpCode as CorporationCode,
    ) as CorpTranslation | undefined;

    const corpRapidstor = (key: string): string | undefined => {
        const value = corpTranslations?.rapidstor?.[key];
        return typeof value === 'string' ? value : undefined;
    };

    switch (helper) {
        case 'payNowButtonText':
        case 'payLaterButtonText':
        case 'saveQuoteButtonText':
            // Corp `rapidstor.<helper>` override wins, else the location setting.
            return corpRapidstor(helper) !== undefined ? 'translation' : 'setting';
        case 'noAvailableUnitText':
            if (corpRapidstor('noAvailableUnitsText') !== undefined) {
                return 'translation';
            }
            // An empty setting falls back to an i18next string, not the setting.
            return location.rapidstor.noAvailableUnitsText ? 'setting' : 'translation';
        case 'showPricesButtonText':
            return corpTranslations?.rapidstor?.showPricesButtonText
                ? 'translation'
                : 'setting';
        case 'showPricesTitleText':
            return corpTranslations?.contact_card?.showPrices?.title
                ? 'translation'
                : 'setting';
        case 'descriptorTitle':
            // descriptorTitle renders the descriptor name (RapidStor content, or
            // a corp rename) only when useDescriptorTitle is on; otherwise it
            // renders a computed size/dimension, which isn't settings text.
            return location.rapidstor.useDescriptorTitle ? 'setting' : 'translation';
        case 'unitTypeTitle':
            // Always includes the unit/descriptor name (RapidStor content),
            // optionally prefixed with a size — so always settings-sourced.
            return 'setting';
        default:
            return 'setting';
    }
};

/**
 * Render a unit type's dimensions (or area) for display.
 *
 * @param unitType RapidStor unit type
 * @param location RapidStor location
 * @param options Presentation options — see {@link DescriptorDimensionsOptions}
 * @returns e.g. `(2ft W/5ft L)`, `2ft x 5ft`, `(2 W/5 L)`, `(50sqft) or 2x5`
 */
export const descriptorDimensions = descriptorDimensionsUtil;

export const unitTypeHasDeal = (unitType: UnitType): boolean => unitType.deal !== undefined;

export const unitTypeHasDiscount = (unitType: UnitType): boolean => unitType.rates.discounted;

/**
 * Whether a card's own attached deal actually saves the customer money. Some `deals[]` entries
 * (e.g. a themed listing like "Wine Storage") carry no discount at all
 * (`rates.discounted: false`, `discountSaved: null`) — those shouldn't be presented with
 * "valued at $X" / savings-percentage discount framing.
 */
export const unitTypeHasMonetaryDiscount = (unitType: UnitType): boolean => !!unitType.deal?.rates.discounted && !!unitType.deal?.rates.discountSaved;

/**
 * `unitType.rates` is a shared base populated from `unitType.deals[0]` (see
 * `unitTypeTransformer`), not from whichever specific `deal` a card was built with. A card
 * whose own deal has no discount (`rates.discounted === false`) must show that deal's own
 * standard rate, not fall through to `unitType.rates` and inherit a different deal's discount.
 */
export const unitTypeDiscountedRate = (unitType: UnitType): number => {
    if (unitType.deal) {
        return unitType.deal.rates.discounted
            ? unitType.deal.rates.discountedRate ?? unitType.deal.rates.rate
            : unitType.deal.rates.rate;
    }
    return unitType.rates.discountedRate;
};

export const unitTypeRate = (unitType: UnitType): number => unitType.rates.rate;

export const unitTypeDisplayRate = (unitType: UnitType): number => (unitType.deal ? unitTypeDiscountedRate(unitType) : unitTypeRate(unitType));

export const unitTypeValueRate = (unitType: UnitType, location: Location): string => formatPriceLabel(formatCurrency(unitTypeRate(unitType), location), location);

export const unitTypeFormattedRate = (unitType: UnitType, location: Location): string => formatPriceLabel(formatCurrency(unitTypeDisplayRate(unitType), location), location);

export const unitTypeDealSavingsPercentage = (unitType: UnitType): string => {
    if (!unitType.deal?.rates.rate || !unitType.deal?.rates.discountSaved) {
        return '0%';
    }
    const val = (unitType.deal.rates.discountSaved / unitType.deal.rates.rate) * 100;
    return `${val.toFixed(0)}%`;
};

export const unitTypeDealInclusions = (unitType: UnitType): string[] => (unitType.deal ? unitType.deal.descriptions : []);

/**
 * Get the date range for the date picker
 *
 * @param location RapidStor location
 * @param type 'movein' or 'reservation'
 * @returns Date Range object used in react-datepicker min and max values
 */
export const getDatePickerRange = (location: Location, type: 'movein' | 'reservation'): Record<string, Date | undefined> => {
    let maxDate = null;

    const openingDate = location.rapidstor.siteOpeningDate
        ? moment(location.rapidstor.siteOpeningDate, 'DD-MM-YYYY')
        : null;

    const minDates = [moment()];
    if (openingDate?.isValid()) {
        minDates.push(openingDate);
    }
    if (location.rapidstor.reservationNumberOfDaysInAdvanceMinimum) {
        minDates.push(moment().add(location.rapidstor.reservationNumberOfDaysInAdvanceMinimum, 'days'));
    }

    let minDate = moment.max(minDates);

    // Check if minDate falls on a closed date and find the next available date
    if (location.rapidstor.datesClosed?.length) {
        const closedDatesSet = new Set(
            location.rapidstor.datesClosed
                .split(',')
                .map((d) => moment(d.trim(), 'DD-MM-YYYY').format('YYYY-MM-DD')),
        );

        // While minDate is in the closed dates, move to the next day
        while (closedDatesSet.has(minDate.format('YYYY-MM-DD'))) {
            minDate = minDate.clone().add(1, 'days');
        }
    }
    const limit = {
        movein: location.rapidstor.moveinNumberOfDaysInAdvanceLimit,
        reservation: location.rapidstor.reservationNumberOfDaysInAdvanceLimit,
    }[type];

    if (limit) {
        // The limit counts from the first day the store takes bookings. Counted from today on a
        // site that opens later, the window ended before it began and every day was disabled.
        const limitStart = openingDate?.isValid() ? moment.max(moment(), openingDate) : moment();
        maxDate = limitStart.clone().add(limit, 'days');
    }

    return {
        min: minDate.toDate(),
        max: maxDate ? maxDate.toDate() : undefined,
    };
};

/**
 * Get the default date for the date picker
 *
 * @param location RapidStor location
 * @param now Moment object representing the current date
 *
 * @returns Default date for the date picker
 */
export const getDefaultNeededDate = (location: Location, now: Moment): Date => {
    const excludedDatesSet = new Set(
        (location.rapidstor.datesClosed ?? '')
            .split(',')
            .map((d) => moment(d.trim(), 'DD-MM-YYYY').format('YYYY-MM-DD')),
    );

    let defaultDate = now.clone();

    if (location.rapidstor.reservationNumberOfDaysInAdvanceMinimum) {
        defaultDate = defaultDate.add(location.rapidstor.reservationNumberOfDaysInAdvanceMinimum, 'days');
    }

    // Ensure the default date is not before the site opening date
    if (location.rapidstor.siteOpeningDate) {
        const siteOpeningDate = moment(location.rapidstor.siteOpeningDate, 'DD-MM-YYYY');
        if (defaultDate.isBefore(siteOpeningDate, 'day')) {
            defaultDate = siteOpeningDate.clone();
        }
    }

    if (excludedDatesSet.has(defaultDate.format('YYYY-MM-DD'))) {
        let nextDate = defaultDate.clone().add(1, 'days');
        while (excludedDatesSet.has(nextDate.format('YYYY-MM-DD'))) {
            nextDate = nextDate.add(1, 'days');
        }
        return nextDate.toDate();
    }

    return defaultDate.toDate();
};

/**
 * The needed date as the API wants it: the calendar day the customer picked, `YYYY-MM-DD`.
 *
 * The date pickers hand back a `Date` at midnight in the *browser's* zone. Sent on as an
 * instant, the API has to guess a zone to read a day back off it, and it guesses with the
 * site's fixed GMT offset — which carries no daylight-saving rule and so is an hour out for
 * every Australian site from October to April. Bookings near midnight were recorded on the
 * previous day. There is no time and no zone in "the 1st of December", so none is sent.
 *
 * Call this wherever a reservation payload is built. A missing date passes through as
 * `undefined`: `save-quote` accepts one, and the string "Invalid date" would be worse.
 */
export const toNeededDate = (
    neededDate: Date | Moment | string | null | undefined,
): string | undefined => {
    if (!neededDate) {
        return undefined;
    }
    const parsed = moment(neededDate);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

/**
 * The reverse of `toNeededDate`: a needed date read back off a reservation, as a `Date` for a
 * datepicker.
 *
 * `new Date("2026-09-10")` parses a bare calendar date as UTC midnight, which renders as the
 * previous day in any browser west of UTC. Moment reads it as local midnight instead — the day
 * the customer picked — so parse it that way before handing it to a picker.
 */
/**
 * The needed date a Book Now PATCH should carry: the one stored by an earlier contact-card
 * submit, from the store first and the cookie second, as `YYYY-MM-DD`.
 *
 * When neither holds a date this is `undefined`, and the PATCH omits the field so the API keeps
 * the date already on the reservation. It used to fall back to `new Date()`: a saved quote on a
 * site that opens later was patched to today, a day the move-in wizard could not book.
 */
export const storedNeededDate = (
    contactData: { neededDate?: string | null } | null | undefined,
    cookies: { contactData?: { neededDate?: string | null } | null } | null | undefined,
): string | undefined => toNeededDate(contactData?.neededDate ?? cookies?.contactData?.neededDate);

export const fromNeededDate = (
    neededDate: Date | Moment | string | null | undefined,
): Date | undefined => {
    if (!neededDate) {
        return undefined;
    }
    const parsed = moment(neededDate);
    return parsed.isValid() ? parsed.toDate() : undefined;
};

/**
 * A reservation is free when the needed (move-in) date is fewer than
 * `reservationNumberOfDaysInAdvanceChargeFee` days away. On or beyond that
 * threshold the reservation fee must be collected.
 *
 * @param location
 * @param neededDate
 * @returns boolean — true when no reservation fee applies
 */
export const isFreeReservation = (
    location: Location,
    neededDate: Date | Moment,
): boolean => moment(neededDate).startOf('day').diff(moment().startOf('day'), 'days')
    < location.rapidstor.reservationNumberOfDaysInAdvanceChargeFee;

/**
 * The reservation-with-fee screen shows the payment form on step 1 and the
 * confirmation panel on step 2.
 *
 * The screen reaches step 2 either by returning from a payment gateway (the
 * `/return` hash) or because the reservation already has its fee charged. The
 * second case matters on a direct visit or a page reload: without it the screen
 * renders the payment form again for a reservation that is already paid, and
 * the customer can attempt a second payment.
 *
 * @param hash — the current `window.location.hash`
 * @param reservation — the loaded reservation, if any
 * @returns boolean — true when the confirmation step must be shown
 */
export const isReservationFeeConfirmationStep = (
    hash: string,
    reservation?: Pick<BaseReservation, 'reservationFeeCharged'>,
): boolean => hash.includes('/return') || reservation?.reservationFeeCharged === true;

export const RapidStorSettings = (location: Location) => ({

    customerType: () => ({
        show: () => location.rapidstor.wizards.moveinSingle.distinguishCustomerTypes,
    }),

    title: () => ({
        show: () => !location.rapidstor.wizards.moveinSingle.hideTenantTitle,
        options: (t: TFunction) => location.rapidstor.wizards.common.titles ?? [
            t('wizard:contactDetails:titles:mr'),
            t('wizard:contactDetails:titles:mrs'),
            t('wizard:contactDetails:titles:miss'),
            t('wizard:contactDetails:titles:ms'),
            t('wizard:contactDetails:titles:dr'),
        ],
    }),

    email: () => ({
        disabled: () => location.rapidstor.wizards.moveinSingle.disableEmailField,
    }),

    dateOfBirth: () => ({
        show: () => !location.rapidstor.wizards.moveinSingle.hideDateOfBirth,
    }),

    state: () => ({
        show: () => !location.rapidstor.wizards.moveinSingle.hideState,
    }),

    postalCode: () => ({
        show: () => !location.rapidstor.wizards.moveinSingle.hidePostalCode,
    }),

    alternateContact: () => ({
        show: () => !location.rapidstor.wizards.moveinSingle.hideAlternativeContact,
        phoneNumber: () => ({
            required: () => location.rapidstor.wizards.moveinSingle.requireAlternativeContactPhoneNumberField,
        }),
        addressDetails: () => ({
            required: () => location.rapidstor.wizards.moveinSingle.requireAlternativeContactAddressDetails,
        }),
        email: () => ({
            required: () => location.rapidstor.wizards.moveinSingle.requireAlternativeContactEmailField,
        }),
    }),

    passwordFields: () => ({
        show: () => !location.rapidstor.wizards.moveinSingle.hidePasswordFields
            && window.rapidstor_settings?.rapidstor_options?.checkoutVariant !== 'simplified',
    }),

    identityVerification: () => ({
        show: () => (
            !location.rapidstor.wizards.moveinSingle.hideDriversLicence
            || location.rapidstor.wizards.moveinSingle.allowIdUpload
        ),
        idUpload: () => ({
            show: () => location.rapidstor.wizards.moveinSingle.allowIdUpload,
        }),
        driversLicense: () => ({
            show: () => !location.rapidstor.wizards.moveinSingle.hideDriversLicence,
            state: () => ({
                show: () => !location.rapidstor.wizards.moveinSingle.hideDriversLicenceState,
            }),
        }),
    }),

    insurance: () => ({
        show: (reservation?: BaseReservation, coverages?: InsuranceCoverage[]) => {
            if (location.rapidstor.wizards.moveinSingle.hideInsurance) {
                return false;
            }

            if (!reservation || reservation.unitType.excludeFromInsurance) {
                return false;
            }

            if (!coverages || coverages.length === 0) {
                return false;
            }

            return true;
        },
        required: () => location.rapidstor.wizards.moveinSingle.insuranceRequired,
        optional: () => !location.rapidstor.wizards.moveinSingle.insuranceRequired,
        manualEntry: () => ({
            required: () => location.rapidstor.wizards.moveinSingle.insuranceManualEntryRequired,
            show: () => location.rapidstor.wizards.moveinSingle.insuranceManualEntry,
        }),
    }),

    proofOfInsurance: () => ({
        show: (insuranceType: string) => {
            if (location.rapidstor.wizards.moveinSingle.hideInsurance) {
                return false;
            }

            if (insuranceType === 'self' || location.rapidstor.wizards.moveinSingle.allowProofOfInsuranceUpload) {
                return true;
            }

            return false;
        },
    }),

    proofOfAddress: () => ({
        show: () => location.rapidstor.wizards.moveinSingle.allowProofOfAddressUpload,
    }),

    boxshop: () => ({
        show: () => !location.rapidstor.wizards.moveinSingle.hideBoxshop,

        coupons: () => ({
            show: () => location.rapidstor.useBoxShopCoupons,
        }),
    }),

    paymentForm: () => ({
        show: () => !location.rapidstor.paymentGateways.use,
    }),

    autobill: () => ({
        show: () => location.rapidstor.useAutoBilling && !location.rapidstor.paymentGateways.use,
        required: () => location.rapidstor.requireAutobill,
        // Legacy allowDirectDebit — the new Location type has no customFeatures.directDebit gate.
        allowDirectDebit: () => location.rapidstor.directDebit.enabled,
        // Legacy allowCreditCardAutobill.
        allowCreditCard: () => location.rapidstor.creditCardForAutobill.enabled,
        // Legacy takingDirectCreditCard — autobill is only available without a payment gateway.
        takingDirectCreditCard: () => !location.rapidstor.paymentGateways.use,
        forceInitialCard: () => location.rapidstor.forceInitialCard,
        allowSwitchingToDebit: () => location.rapidstor.tenantAccounts.autoPayment.enableSwitchingToDebit,
        // Legacy default autobill type (services.js 2080-2084): '' none, 'cc', 'dd'.
        defaultType: (isSimplified: boolean): string => {
            const takingDirectCreditCard = !location.rapidstor.paymentGateways.use;
            const checkedOrRequired = location.rapidstor.autoBillCheckedByDefault
                || location.rapidstor.requireAutobill;

            if (isSimplified) {
                // Simplified only supports credit-card autobill via the main payment card.
                return takingDirectCreditCard && checkedOrRequired ? 'cc' : '';
            }

            if (
                takingDirectCreditCard
                && location.rapidstor.directDebit.enabled
                && location.rapidstor.autoBillCheckedByDefault
            ) {
                return 'dd';
            }

            return takingDirectCreditCard && checkedOrRequired ? 'cc' : '';
        },
    }),

    driversLicense: () => ({
        show: () => !location.rapidstor.wizards.moveinSingle.hideDriversLicence,
    }),

    licencePlate: () => ({
        show: () => !location.rapidstor.wizards.moveinSingle.hideLicencePlate,
    }),

    assistanceMoving: () => ({
        show: () => location.rapidstor.assistanceMoving,
    }),

    dangerousGoodsWarning: () => ({
        show: () => location.rapidstor.wizards.moveinSingle.showDangerousGoodsWarning,
        value: () => location.rapidstor.dangerousGoodsWarning,
    }),

    showSaveQuoteButton: () => location.rapidstor.allowSaveQuote,

    showReservationButton: () => location.rapidstor.allowReservation,

    showMoveInButton: () => location.rapidstor.allowMoveIn,

    showDealCoupons: () => location.rapidstor.useDealCoupons,

    alwaysAllowPricingPage: () => location.rapidstor.alwaysAllowPricingPage,

    country: () => location.rapidstor.country,
});

/**
 * Cookie that marks a persona as "locked" — i.e. pinned by config/options or the URL rather
 * than chosen by the user on the Index screen. Only a locked persona removes the persona step.
 */
export const PERSONA_LOCK_COOKIE = 'personaLocked';

/** True when the persona-lock marker cookie is present. */
const hasPersonaLock = (): boolean => document.cookie
    .split('; ')
    .some((c) => c.startsWith(`${PERSONA_LOCK_COOKIE}=`));

/**
 * Determines if the Personal & Business step (first step) is skipped
 * This happens when storageType page is disabled or persona is enabled
 */
export const isFirstStepSkipped = (): boolean => {
    if (!window.rapidstor_settings?.app_config) {
        return false;
    }

    if (!window.rapidstor_settings.app_config.pages.storageType) {
        return true;
    }

    // Check config-level persona
    if (window.rapidstor_settings.app_config.journey.persona) {
        return true;
    }

    // Check persona from options (set via data attribute or config)
    if (window.rapidstor_settings.rapidstor_options?.persona) {
        return true;
    }

    // Check personaId query param (before cookie is set on first load)
    const queryParams = new URLSearchParams(window.location.search);
    const hashQuery = window.location.hash.split('?')[1];
    if (queryParams.get('personaId') || (hashQuery && new URLSearchParams(hashQuery).get('personaId'))) {
        return true;
    }

    // Check the persona-lock marker (set for config/options/URL personas, not user selection).
    // A bare `persona` cookie from a user picking a persona on the Index screen does NOT lock the
    // step, so they can return to it and choose a different one.
    if (hasPersonaLock()) {
        return true;
    }

    return false;
};

/**
 * Calculates the correct journey step number for a given page
 * @param pageType - The type of page: 'unit-listing' | 'book-unit' | 'customise-booking'
 * @returns The actual step number in the journey
 */
export const getJourneyStep = (pageType: 'unit-listing' | 'book-unit' | 'customise-booking'): number => {
    // The actual step numbers in the journey are always:
    // 1. Personal & Business (can be skipped)
    // 2. Storage Size (unit-listing)
    // 3. Pricing (book-unit)
    // 4. Customise Booking (customise-booking)

    switch (pageType) {
        case 'unit-listing':
        // Unit-listing is always step 2, regardless of whether first step is skipped
            return 2;
        case 'book-unit':
        // Book-unit is always step 3, regardless of whether first step is skipped
            return 3;
        case 'customise-booking':
        // Customise-booking is always step 3 (same as book-unit for mobile, step 4 for desktop)
            return 3;
        default:
            return 1;
    }
};
